import { Extension } from '@tiptap/core'
import { Node } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import {
  CharacterCounterMethod,
  CharacterCountOptions,
  CharacterCountStorage,
} from './types'

const getCharacterCount = (
  rootNode: Node,
  counterMethod?: CharacterCounterMethod | null
): number => {
  const [from, to] = [0, rootNode.content.size]
  const text = rootNode.textBetween(from, to, ' ', ' ')
  const actualCount = text.length

  // Twitter/X uses a fixed count of 23 for any URL, because they
  // replace every URL with a link to their shortener service
  if (counterMethod === CharacterCounterMethod.TwitterX) {
    // This is a pretty permissive URL regex
    // We want to catch any text that _looks_ like a URL
    const urlRegex =
      /(http(s)?:\/\/.)?(www\.)?[-a-z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-z0-9@:%_+.~#?&//=]*)/gi
    const matchedUrls = [...text.matchAll(urlRegex)]

    // Add 23 characters for each matched URL, then
    // subtract the lengths of the original URLs
    return (
      actualCount +
      23 * matchedUrls.length -
      matchedUrls.reduce((len, [url]) => len + url.length, 0)
    )
  }

  return actualCount
}

declare module '@tiptap/core' {
  interface Storage {
    characterCount: CharacterCountStorage
  }
}

export const CharacterCount = Extension.create<
  CharacterCountOptions,
  CharacterCountStorage
>({
  name: 'characterCount',

  // Default options
  addOptions() {
    return {
      limit: null,
      counterType: null,
      isSoftLimit: false,
      overLimitClassName: 'text-over-limit',
    }
  },

  addStorage() {
    return {
      characters: 0,
    }
  },

  addProseMirrorPlugins() {
    const { counterMethod, limit, isSoftLimit, overLimitClassName } =
      this.options

    const applyOverlimitDecoration = (node: Node): DecorationSet | null => {
      if (!limit || isSoftLimit || this.storage.characters <= limit) {
        return null
      }

      // When using a counter method like TwitterX, the weighted character
      // count may differ from the actual text length (e.g., URLs counted
      // as 23 chars). Offset the decoration start so it lands at the
      // correct position in the actual document text.
      const countAdjustment = this.storage.characters - getCharacterCount(node)
      const startPosition = Math.max(1, limit + 1 - countAdjustment)

      return DecorationSet.create(node, [
        Decoration.inline(startPosition, node.content.size, {
          class: overLimitClassName,
        }),
      ])
    }

    return [
      new Plugin<ReturnType<typeof applyOverlimitDecoration>>({
        key: new PluginKey('characterCount'),
        state: {
          // Apply decorations when document is initialized
          init: (_, { doc }) => {
            this.storage.characters = getCharacterCount(doc, counterMethod)
            return applyOverlimitDecoration(doc)
          },
          // And re-apply when the document changes
          apply: (tr, old) => {
            if (tr.docChanged) {
              this.storage.characters = getCharacterCount(tr.doc, counterMethod)
              return applyOverlimitDecoration(tr.doc)
            }

            return old
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)
          },
        },
      }),
    ]
  },
})

export type { CharacterCountOptions }
